import React from 'react';
import { NamedLink } from '../../components';
import css from './ListingPage.module.css';

/**
 * Breadcrumb trail above the listing's hero image.
 *
 *   Home › Materials › Paint & Finish › Wall Paint › Verification Paint v2
 *
 * Each non-final segment is a NamedLink; the listing title sits at the
 * tail as plain text. Category labels are resolved from
 * config.categoryConfiguration.categories (the tree). Levels missing
 * from the tree (or from the listing's publicData) are silently
 * skipped — defensive against stale taxonomy IDs without leaking
 * invalid IDs into the UI.
 */

// Hosted Sharetribe Console category config uses `name`; the local
// fallback in src/config/configCategories.js uses `label`. Read either
// so the breadcrumb works regardless of which source feeds runtime
// config.categoryConfiguration.
const labelOf = c => c?.name || c?.label || c?.id || '';

// Walk the category tree once to find the {label, search params} for
// each level. Returns an array of segments {label, params} that the
// caller maps into NamedLink params.
const buildSegments = (publicData, categoriesConfig) => {
  if (!publicData || !Array.isArray(categoriesConfig)) return [];
  const { categoryLevel1, categoryLevel2, categoryLevel3 } = publicData;
  const segments = [];

  const l1 = categoriesConfig.find(c => c.id === categoryLevel1);
  if (!l1) return segments;
  segments.push({ label: labelOf(l1), params: { pub_categoryLevel1: l1.id } });

  const l2 = l1.subcategories?.find(c => c.id === categoryLevel2);
  if (!l2) return segments;
  segments.push({
    label: labelOf(l2),
    params: { pub_categoryLevel1: l1.id, pub_categoryLevel2: l2.id },
  });

  const l3 = l2.subcategories?.find(c => c.id === categoryLevel3);
  if (!l3) return segments;
  segments.push({
    label: labelOf(l3),
    params: {
      pub_categoryLevel1: l1.id,
      pub_categoryLevel2: l2.id,
      pub_categoryLevel3: l3.id,
    },
  });

  return segments;
};

const Breadcrumbs = props => {
  const { publicData, categoriesConfig, title } = props;
  const segments = buildSegments(publicData, categoriesConfig);

  return (
    <nav className={css.breadcrumbs} aria-label="Breadcrumb">
      <ol className={css.breadcrumbsList}>
        {/* First layer is L1 — Home/landing-page link removed.
            The page topbar's logo already provides the path back to /. */}
        {segments.map((seg, i) => (
          <li key={i} className={css.breadcrumbItem}>
            <NamedLink
              className={css.breadcrumbLink}
              name="SearchPage"
              to={{ search: new URLSearchParams(seg.params).toString() }}
            >
              {seg.label}
            </NamedLink>
            <span className={css.breadcrumbSep} aria-hidden="true">›</span>
          </li>
        ))}
        {title ? (
          <li className={`${css.breadcrumbItem} ${css.breadcrumbCurrent}`} aria-current="page">
            {title}
          </li>
        ) : null}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
